import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';
import { ReactComponent as EatClubIcon } from '../../assets/icons/eatclub_logo.svg';
import { ReactComponent as EatClubIconColored } from '../../assets/icons/eatclub_logo_colored.svg';
import { ReactComponent as ObeeIcon } from '../../assets/icons/obee_logo.svg';
import { ReactComponent as ObeeBlackIcon } from '../../assets/icons/obee_logo_black.svg';
import { ReactComponent as ObeeWhiteIcon } from '../../assets/icons/obee_logo_white.svg';
import { colors, typography } from '../../theme';
import { momentDaysOfWeek } from '../../utils/constants';
import { devLog } from '../../utils/devLog';
import { isEmpty, isNil, nullIfNegative, onlyUnique, trim } from '../../utils/helpers';
import { safeParse } from '../../utils/stringHelpers';
import { getContrastTextColor } from '../../utils/themeHelpers';
import { obeeAreaPropTypes } from './obeeAreas';

const restaurantPropType = PropTypes.shape({
  objectId: PropTypes.string,
  name: PropTypes.string,
  address: PropTypes.string,
  phone: PropTypes.string,
  email: PropTypes.string,
  slug: PropTypes.string,
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  premium: PropTypes.bool,
  theme: PropTypes.string,
  daysInAdvance: PropTypes.number,
  areaPhotos: PropTypes.objectOf(
    PropTypes.shape({
      imageLink: PropTypes.string,
      description: PropTypes.string,
    }),
  ),
  waitlist: PropTypes.bool,
  ccCapture: PropTypes.bool,
  ccNoShowFee: PropTypes.number,
  ccPaymentPolicy: PropTypes.string,
  ccWaitlistShow: PropTypes.string,
  ccWaitlistPolicy: PropTypes.string,
  terms: PropTypes.string,
  closures: PropTypes.arrayOf(
    PropTypes.shape({
      restId: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      startTime: PropTypes.number,
      endTime: PropTypes.number,
    }),
  ),
  timezone: PropTypes.string,
  googleTagManager: PropTypes.string,
  facebookMetaPixel: PropTypes.string,

  // Appearance settings
  generalFontFamilies: PropTypes.string,

  navigationLogoUrl: PropTypes.string,
  navigationLogoAlignment: PropTypes.oneOf(['left', 'middle']),

  navigationShowVenueName: PropTypes.bool,
  navigationVenueNameTextColour: PropTypes.string,

  navigationBgColour: PropTypes.string,
  navigationBgColourTransparency: PropTypes.number,

  navigationShowUnderline: PropTypes.bool,
  navigationUnderlineColour: PropTypes.string,
  navigationUnderlineHeight: PropTypes.number,

  navigationShowHeaderText: PropTypes.bool,
  navigationHeaderText: PropTypes.string,
  navigationHeaderTextColour: PropTypes.string,

  navigationShowNeedHelp: PropTypes.bool,
  navigationNeedHelpButtonTextColour: PropTypes.string,
  navigationNeedHelpButtonBorderColour: PropTypes.string,

  backgroundType: PropTypes.oneOf(['solid', 'image']),
  backgroundValue: PropTypes.string,
  backgroundImageUrl: PropTypes.string,

  availabilityFontFamilies: PropTypes.string,

  availabilityMainButtonBgColour: PropTypes.string,
  availabilityMainButtonTextColour: PropTypes.string,
  availabilityMainButtonCornerRadius: PropTypes.number,

  availabilityTimeButtonBgColour: PropTypes.string,
  availabilityTimeButtonTextColour: PropTypes.string,
  availabilityTimeButtonCornerRadius: PropTypes.number, // not used

  availabilityFieldsActiveStateColour: PropTypes.string,
  availabilityFieldsTextColour: PropTypes.string,
  availabilityFieldsColourOpacity: PropTypes.number,
  availabilityFieldsOutlineColour: PropTypes.string,
  availabilityFieldsActiveOutlineColour: PropTypes.string,

  availabilityBackgroundBoxBgColour: PropTypes.string,
  availabilityBackgroundBoxBorderColour: PropTypes.string,
  availabilityBackgroundBoxBorderWeight: PropTypes.number,

  secondaryActiveBackgroundColor: PropTypes.string,

  showProviderLogo: PropTypes.bool,
  providerLogoStyle: PropTypes.oneOf(['coloured', 'white', 'black']),
  providerLogoAlignment: PropTypes.oneOf(['left', 'right']),

  bodyTextColour: PropTypes.string,
  bodyButtonTextColour: PropTypes.string,
  bodyButtonBorderColour: PropTypes.string,

  dateSelectorType: PropTypes.oneOf(['7_days', 'calendar', 'calendar_always', 'experiences']),

  // Areas settings
  areas: PropTypes.arrayOf(obeeAreaPropTypes),

  // Data collection settings
  showHighchairs: PropTypes.bool,
  showChildren: PropTypes.bool,
  showMarketingOptIn: PropTypes.bool,
  marketingOptInMessage: PropTypes.string,

  // Venue info settings
  venueMessage: PropTypes.string,
  groupSizeMessage: PropTypes.string,

  // Multi-venue settings
  suggestAvailability: PropTypes.string,
  multiVenueImageUrl: PropTypes.string,
  venueDescription: PropTypes.string,

  // Confirmation
  confirmationImageUrl: PropTypes.string,

  // Experience theming
  experienceActiveBackgroundColor: PropTypes.string,
  experienceActiveTextColor: PropTypes.string,
  dietaryTagBackgroundColor: PropTypes.string,
  dietaryTagTextColor: PropTypes.string,
  dietaryTagBorderColor: PropTypes.string,
  dietaryTagActiveBackgroundColor: PropTypes.string,
  dietaryTagActiveTextColor: PropTypes.string,
  dietaryTagActiveBorderColor: PropTypes.string,
  showDietaryRequirements: PropTypes.bool,
  dietaryTagsMandatory: PropTypes.bool,

  showPostcodeField: PropTypes.bool,
  postcodeFieldMandatory: PropTypes.bool,
  showVoucherField: PropTypes.bool,
  showOverfilledGuests: PropTypes.bool,

  dividerColor: PropTypes.string,
  addVoucherLabel: PropTypes.string,
  addVoucherSubtext: PropTypes.string,

  // Settings not fetched from the api
  iconBgColour: PropTypes.string,
  modalHeaderColour: PropTypes.string,
  widgetHeaderBgColor: PropTypes.string,
});

export const restaurantPropTypes = PropTypes.shape({
  data: restaurantPropType,
  error: PropTypes.string,
  pending: PropTypes.bool,
  success: PropTypes.bool,
});

export const restaurantsPropTypes = PropTypes.shape({
  data: PropTypes.arrayOf(restaurantPropType),
  error: PropTypes.string,
  pending: PropTypes.bool,
  success: PropTypes.bool,
});

/**
 * Some settings have a premium wall
 * @param {boolean} isPremium
 * @param {*} premiumSetting
 * @param {*} liteSetting the fallback if `isPremium` is false or `premiumSetting` is null/undefined
 * @returns
 */
const getPremiumSetting = (isPremium, premiumSetting, liteSetting = null) => {
  if (isPremium) {
    return premiumSetting ?? liteSetting;
  }

  return liteSetting;
};

/**
 * Parse an int from the server and allow for defaults.
 * Will always default to 0 if could not parse as int.
 *
 * @param value
 * @param defaultValue
 * @returns {number|number|number}
 */
const extractInt = (value, defaultValue = 0) => {
  // backend converts 1s and 0s to true and false...
  if (value === false) {
    return 0;
  }

  if (value === true) {
    return 1;
  }

  if (isNil(value)) {
    return defaultValue;
  }

  return parseInt(value, 10) || 0;
};

/**
 * Ensure the value being read is treated as a number and suffixes such as "px" are removed
 * @param value
 * @param suffixToRemove
 * @param defaultValue
 */
const getValueAsNumber = (value, suffixToRemove = '%', defaultValue = 0) => {
  const cleaned =
    typeof value === 'string' ? value.replace('#', '').replace(suffixToRemove, '') : value;
  return extractInt(cleaned, defaultValue);
};

const getAppearanceSettings = (isPremium, theme) => {
  const availabilityMainButtonBgColour = getPremiumSetting(
    isPremium,
    theme.availabilityMainButtonBgColour ?? colors.premiumAvailabilityMainButtonBgColour,
    colors.liteAvailabilityMainButtonBgColour,
  );

  const customFonts = getPremiumSetting(isPremium, safeParse(theme?.extraCustomFonts, []), []);

  // Get the custom font as well as its fallback fonts if it has any
  const customGeneralFontFamilies = customFonts
    .filter((font) => font?.defaultFont && font?.text)
    .map((font) =>
      [`'${font?.fontName}'`, font?.fallbackFonts].filter((item) => !isEmpty(item)).join(', '),
    );

  // Support custom fonts specific to numbers
  const customNumberFontFamilies = customFonts
    .filter((font) => font?.numbers)
    .map((font) =>
      [`'${font?.fontName}'`, font?.fallbackFonts].filter((item) => !isEmpty(item)).join(', '),
    );

  return {
    // General

    // Apply any custom fonts, then the chosen font
    generalFontFamilies: getPremiumSetting(
      isPremium,
      [...customGeneralFontFamilies, theme.generalFontFamilies].join(', '),
      typography.generalFontFamily,
    ),

    numberFontFamilies: getPremiumSetting(
      isPremium,
      [...customNumberFontFamilies, theme.numberFontFamilies || theme.generalFontFamilies].join(
        ', ',
      ),
      typography.numberFontFamily,
    ),

    customFonts: getPremiumSetting(isPremium, safeParse(theme?.extraCustomFonts, []), []),

    // Navigation
    navigationLogoUrl: getPremiumSetting(isPremium, theme.navigationLogoUrl),
    navigationLogoAlignment: getPremiumSetting(isPremium, theme.navigationLogoAlignment, 'left'), // left and center

    navigationShowVenueName: getPremiumSetting(isPremium, theme.navigationShowVenueName, true),
    navigationVenueNameTextColour: getPremiumSetting(
      isPremium,
      theme.navigationVenueNameTextColour,
      colors.black,
    ),

    navigationBgColour: getPremiumSetting(isPremium, theme.navigationBgColour, 'transparent'),
    navigationBgColourTransparency: getPremiumSetting(
      isPremium,
      getValueAsNumber(theme.navigationBgColourTransparency, '%', 100),
      100,
    ),

    navigationShowUnderline: getPremiumSetting(isPremium, theme.navigationShowUnderline, true),
    navigationUnderlineColour: getPremiumSetting(
      isPremium,
      theme.navigationUnderlineColour,
      colors.liteNavigationUnderlineColour,
    ),
    navigationUnderlineHeight: getPremiumSetting(
      isPremium,
      getValueAsNumber(theme.navigationUnderlineHeight, 'px', 1),
      1,
    ),

    navigationShowHeaderText: getPremiumSetting(isPremium, theme.navigationShowHeaderText, true),
    navigationHeaderText: getPremiumSetting(isPremium, theme.navigationHeaderText, 'Reservations'),
    navigationHeaderTextColour: getPremiumSetting(isPremium, theme.navigationHeaderTextColour),

    navigationShowNeedHelp: getPremiumSetting(isPremium, theme.navigationShowNeedHelp, true),
    navigationNeedHelpButtonTextColour: getPremiumSetting(
      isPremium,
      theme.navigationNeedHelpButtonTextColour,
    ),
    navigationNeedHelpButtonBorderColour: getPremiumSetting(
      isPremium,
      theme.navigationNeedHelpButtonBorderColour,
    ),

    // Form header
    formHeaderBackgroundColor: getPremiumSetting(isPremium, theme?.formHeaderBackgroundColor, null),
    formHeaderUnderlineColor: getPremiumSetting(isPremium, theme.formHeaderUnderlineColor, null),

    // Background
    backgroundType: getPremiumSetting(isPremium, theme.backgroundType, 'solid'),
    backgroundValue: getPremiumSetting(
      isPremium,
      theme.backgroundValue,
      colors.liteBackgroundValue,
    ),
    backgroundImageUrl: getPremiumSetting(isPremium, theme.backgroundImageUrl, null),

    // Availability
    availabilityFontFamilies: getPremiumSetting(
      isPremium,
      [...customGeneralFontFamilies, theme.availabilityFontFamilies].join(', '),
      typography.availabilityFontFamily,
    ),

    availabilityMainButtonBgColour,
    availabilityMainButtonTextColour: getPremiumSetting(
      isPremium,
      theme.availabilityMainButtonTextColour ??
        getContrastTextColor(availabilityMainButtonBgColour),
    ),
    availabilityMainButtonCornerRadius: getPremiumSetting(
      isPremium,
      getValueAsNumber(theme.availabilityMainButtonCornerRadius, 'px'),
      '6px',
    ),

    availabilityFieldsActiveStateColour: getPremiumSetting(
      isPremium,
      theme.availabilityFieldsActiveStateColour,
      colors.liteAvailabilityFieldsActiveStateColour,
    ),
    availabilityFieldsColourOpacity: getPremiumSetting(
      isPremium,
      getValueAsNumber(theme.availabilityFieldsColourOpacity, '%', 100),
      100,
    ),
    availabilityFieldsOutlineColour: getPremiumSetting(
      isPremium,
      theme.availabilityFieldsOutlineColour,
      null,
    ),
    availabilityFieldsActiveOutlineColour: getPremiumSetting(
      isPremium,
      theme.availabilityFieldsActiveOutlineColour,
      null,
    ),

    availabilityBackgroundBoxBgColour: getPremiumSetting(
      isPremium,
      theme.availabilityBackgroundBoxBgColour,
      colors.white,
    ),
    availabilityBackgroundBoxBorderColour: getPremiumSetting(
      isPremium,
      theme.availabilityBackgroundBoxBorderColour,
      'transparent',
    ),
    availabilityBackgroundBoxBorderWeight: getPremiumSetting(
      isPremium,
      getValueAsNumber(theme.availabilityBackgroundBoxBorderWeight, 'px'),
      '0',
    ),

    secondaryActiveBackgroundColor: getPremiumSetting(
      isPremium,
      theme.secondaryActiveBackgroundColor ?? colors.liteAvailabilityFieldsActiveStateColour, // TODO add secondaryActiveBackgroundColor to settings
      colors.liteAvailabilityFieldsActiveStateColour,
    ),

    experienceActiveBackgroundColor: getPremiumSetting(
      isPremium,
      theme.experienceActiveBackgroundColor,
      colors.liteAvailabilityFieldsActiveStateColour,
    ),

    experienceActiveTextColor: getPremiumSetting(
      isPremium,
      theme.experienceActiveTextColor,
      colors.cloud,
    ),

    dietaryTagBackgroundColor: getPremiumSetting(
      isPremium,
      theme.dietaryTagBackgroundColor,
      'transparent',
    ),
    dietaryTagTextColor: getPremiumSetting(
      isPremium,
      theme.dietaryTagTextColor,
      theme.mainTextColour,
    ),
    dietaryTagBorderColor: getPremiumSetting(
      isPremium,
      theme.dietaryTagBorderColor,
      theme.mainTextColour,
    ),

    dietaryTagActiveBackgroundColor: getPremiumSetting(
      isPremium,
      theme.dietaryTagActiveBackgroundColor,
      colors.liteAvailabilityFieldsActiveStateColour,
    ),
    dietaryTagActiveTextColor: getPremiumSetting(
      isPremium,
      theme.dietaryTagActiveTextColor,
      colors.black, // TODO better support for dark theme
    ),
    dietaryTagActiveBorderColor: getPremiumSetting(
      isPremium,
      theme.dietaryTagActiveBorderColor,
      colors.black, // TODO better support for dark theme
    ),

    showDietaryRequirements: getPremiumSetting(
      isPremium,
      theme.showDietaryRequirements ?? false,
      false,
    ),
    dietaryTagsMandatory: getPremiumSetting(isPremium, theme.dietaryTagsMandatory ?? false, false),

    // These should probably be on the experiences instead of the settings
    selectTimeLabel: getPremiumSetting(
      isPremium,
      theme.selectTimeLabel ?? 'Select a time',
      'Select a time',
    ),
    arrivalTimeLabel: getPremiumSetting(isPremium, theme.arrivalTimeLabel ?? 'Arrival', 'Arrival'),

    showPostcodeField: getPremiumSetting(isPremium, theme.showPostcodeField, false),
    postcodeFieldMandatory: getPremiumSetting(isPremium, theme.postcodeFieldMandatory, false),
    showVoucherField: getPremiumSetting(isPremium, theme.showVoucherField, false),

    dividerColor: getPremiumSetting(isPremium, theme.dividerColour, colors.charcoal),

    showProviderLogo: getPremiumSetting(isPremium, theme.showProviderLogo, true),
    providerLogoStyle: getPremiumSetting(isPremium, theme.providerLogoStyle, 'coloured'),
    providerLogoAlignment: getPremiumSetting(isPremium, theme.providerLogoAlignment, 'left'),

    // Body
    bodyTextColour: getPremiumSetting(isPremium, theme.bodyTextColour),
    bodyButtonTextColour: getPremiumSetting(isPremium, theme.bodyButtonTextColour),
    bodyButtonBorderColour: getPremiumSetting(isPremium, theme.bodyButtonBorderColour),

    dateSelectorType: getPremiumSetting(isPremium, theme.dateSelectorType, 'calendar'),

    // Main text overrides
    darkTheme: getPremiumSetting(isPremium, theme.darkTheme, false),
    mainTextColour: getPremiumSetting(isPremium, theme.mainTextColour, '#313131'),
    // iconColour: getPremiumSetting(isPremium, theme.iconColour, '#313131'),
    outlineColour: getPremiumSetting(isPremium, theme.outlineColour, '#E6E6E6'),
    outlineOpacity: getPremiumSetting(
      isPremium,
      getValueAsNumber(theme.outlineOpacity, '%', 100),
      100,
    ),
    activeOutlineColour: getPremiumSetting(isPremium, theme.activeOutlineColour, '#313131'),
    secondaryTextColour: getPremiumSetting(isPremium, theme.secondaryTextColour, '#6E6E6D'),
    secondaryTextOpacity: getPremiumSetting(
      isPremium,
      getValueAsNumber(theme.secondaryTextOpacity, '%', 100),
      100,
    ),
    // availabilityFieldsActiveStateTextColour: getPremiumSetting(
    //   isPremium,
    //   theme.availabilityFieldsActiveStateTextColour ?? 'lime',
    //   '#313131',
    // ),

    // Messaging

    showCustomMessages: getPremiumSetting(isPremium, theme.showCustomMessages, false),
    venueClosedMessage: getPremiumSetting(isPremium, theme.venueClosedMessage, null),
    showNoAvailabilityDayMessage: getPremiumSetting(
      isPremium,
      theme.showNoAvailabilityDayMessage,
      false,
    ),
    noAvailabilityDayMessage: getPremiumSetting(isPremium, theme.noAvailabilityDayMessage, null),
    showNoAvailabilityTimeMessage: getPremiumSetting(
      isPremium,
      theme.showNoAvailabilityTimeMessage,
      false,
    ),
    noAvailabilityTimeMessage: getPremiumSetting(isPremium, theme.noAvailabilityTimeMessage, null),
    showNoAvailabilityExperienceMessage: getPremiumSetting(
      isPremium,
      theme.showNoAvailabilityExperienceMessage,
      false,
    ),
    noAvailabilityExperienceMessage: getPremiumSetting(
      isPremium,
      theme.noAvailabilityExperienceMessage,
      null,
    ),
    showCutoffMessage: getPremiumSetting(isPremium, theme.showCutoffMessage, false),
    cutoffMessage: getPremiumSetting(isPremium, theme?.cutoffMessage, null),
    showMaxGroupMessage: getPremiumSetting(isPremium, theme?.showMaxGroupMessage, false),
    maxGroupMessage: getPremiumSetting(isPremium, theme?.maxGroupMessage, null),
    showVenueInfoOnCutoff: getPremiumSetting(isPremium, theme?.showVenueInfoOnCutoff, false),
    cutoffContactType: getPremiumSetting(isPremium, theme?.cutoffContactType, 'email'),

    showOverfilledGuests: getPremiumSetting(isPremium, theme?.showOverfilledGuests ?? true, true),
    useVouchersOnPayLater: getPremiumSetting(
      isPremium,
      theme?.useVouchersOnPayLater ?? false,
      false,
    ),

    addVoucherLabel: theme?.addVoucherLabel ?? 'Add voucher',
    addVoucherSubtext: theme?.addVoucherSubtext ?? null,
  };
};

const getAreasSettings = (isPremium, theme) => {
  // TODO widget settings in foh to limit this on standard
  if (!isPremium) {
    return { areaPhotos: {} };
  }

  const areas = safeParse(theme?.areas);

  if (!areas) {
    return { areaPhotos: {} };
  }

  // Fall back to the restaurant's area photos for EC Bookings
  const areaPhotosToUse = areas;

  // If not showing area images, still show the descriptions
  if (!theme?.showAreaImages) {
    Object.keys(areaPhotosToUse).forEach((areaId) => {
      areaPhotosToUse[areaId].imageLink = '';
    });
  }

  return {
    areaPhotos: getPremiumSetting(isPremium, areaPhotosToUse, {}),
  };
};

// TODO get high chairs from a single location
const getDataCollectionSettings = (isPremium, theme, restaurant) => {
  return {
    showHighchairs: restaurant?.showHighchairs || theme?.showHighChairs,
    showChildren: restaurant?.showChildren || theme?.showChildren,
    showMarketingOptIn: theme?.showMarketingOptIn,
    marketingOptInMessage: theme?.marketingOptInMessage,
  };
};

const getVenueInfoSettings = (isPremium, theme) => {
  return {
    venueMessage: getPremiumSetting(isPremium, theme?.venueMessage, null),
    confirmationImageUrl: getPremiumSetting(isPremium, theme?.confirmationImageUrl, null),
  };
};

const getHelpfulInfoSettings = (isPremium, theme) => {
  return {
    displayHelpfulInfo: theme?.displayHelpfulInfo,
    hasDisabledAccess: theme?.hasDisabledAccess,
    hasDressCode: theme?.hasDressCode,
    hasKidsMenu: theme?.hasKidsMenu,
    hasPlayArea: theme?.hasPlayArea,
    hasBYO: theme?.hasBYO,
    hasCorkage: theme?.hasCorkage,
    hasCakes: theme?.hasCakes,
    disabledOption: theme?.disabledOption,
    dressCodeOption: theme?.dressCodeOption,
    kidsMenuOption: theme?.kidsMenuOption,
    playAreaOption: theme?.playAreaOption,
    byoOption: theme?.byoOption,
    corkageOption: theme?.corkageOption,
    corkageFee: theme?.corkageFee,
    corkagePer: theme?.corkagePer,
    cakesOption: theme?.cakesOption,
    compulsoryAreaSelection: theme?.compulsoryAreaSelection,
  };
};

const getMultiVenueSettings = (isPremium, theme) => {
  // This makes it backwards compatible since it used to be a boolean
  const availabilitySetting =
    theme.suggestAvailability === true ? 'no_availability' : theme.suggestAvailability;

  return {
    multiVenueImageUrl: getPremiumSetting(isPremium, theme.multiVenueImageUrl, null),
    suggestAvailability: getPremiumSetting(isPremium, availabilitySetting, 'no'),
  };
};

// TODO move messaging settings from other functions here
const getMessagingSettings = (isPremium, theme) => {
  return {
    dietaryMessage: getPremiumSetting(
      isPremium,
      theme?.dietaryMessage,
      'Please note that dietary requirements must be provided before the dining date, otherwise our kitchen may not be able to meet them.',
    ),
    paymentDueTodayText: getPremiumSetting(
      isPremium,
      theme?.paymentDueTodayText,
      'Amount due today',
    ),
    paymentDueLaterText: getPremiumSetting(
      isPremium,
      theme?.paymentDueLaterText,
      'Amount due later',
    ),
    percentagePaidText: getPremiumSetting(
      isPremium,
      theme?.percentagePaidText,
      'to be paid onsite',
    ),

    // Confirmation screen titles
    confirmationTitle: getPremiumSetting(
      isPremium,
      theme?.confirmationTitle,
      'Thank you, your table has been reserved',
    ),
    confirmationEditingTitle: getPremiumSetting(
      isPremium,
      theme?.confirmationEditingTitle,
      'Your booking is coming up soon',
    ),
    confirmationPastBookingTitle: getPremiumSetting(
      isPremium,
      theme?.confirmationPastBookingTitle,
      'Thank you for dining with us',
    ),
    confirmationFinishedTitle: getPremiumSetting(
      isPremium,
      theme?.confirmationFinishedTitle,
      'Thank you for dining with us',
    ),
    confirmationCancelledTitle: getPremiumSetting(
      isPremium,
      theme?.confirmationCancelledTitle,
      'Your booking has been cancelled',
    ),
    confirmationUnconfirmedTitle: getPremiumSetting(
      isPremium,
      theme?.confirmationUnconfirmedTitle,
      'Your waitlist request has been cancelled',
    ),
    confirmationConfirmedTitle: getPremiumSetting(
      isPremium,
      theme?.confirmationConfirmedTitle,
      'Your booking is confirmed',
    ),
    confirmationWaitlistTitle: getPremiumSetting(
      isPremium,
      theme?.confirmationWaitlistTitle,
      "You're on the waitlist",
    ),
  };
};

/**
 * Gets widget premium and default settings
 * @param {*} restaurant
 * @param {Object|null} previewOverrides draft settings from the partner portal preview mode, merged over the saved theme
 * @returns {*} The widget premium settings if premium, and the default settings if the settings have not been set
 */
export const getWidgetSettings = (restaurant = {}, previewOverrides = null) => {
  const isPremium = restaurant.premium;
  // Theme and settings now come back from the API as two different properties, so let's combine them back into 1
  const theme = {
    ...(safeParse(restaurant.bookingWidgetSettings)?.themeSettings?.themeDetails ?? {}),
    ...(safeParse(restaurant.bookingWidgetSettings)?.widgetSettings?.settingsDetails ?? {}),
    ...(previewOverrides ?? {}),
  };

  devLog('info', `theme from api`, theme);

  const widgetSettings = {
    ...getAppearanceSettings(isPremium, theme),
    ...getAreasSettings(isPremium, theme),
    ...getDataCollectionSettings(isPremium, theme, restaurant),
    ...getVenueInfoSettings(isPremium, theme),
    ...getHelpfulInfoSettings(isPremium, theme),
    ...getMultiVenueSettings(isPremium, theme),
    ...getMessagingSettings(isPremium, theme),
  };

  /**
   * Find whether an address is valid.
   * It's common for addresses to just be blank text with a comma if not properly set.
   * In those cases, we'd rather just have a null value for the address
   * @param address
   * @returns {boolean}
   */
  const isValidAddress = (address) => {
    const trimmedAddress = trim(address);
    return !(isEmpty(trimmedAddress) || trimmedAddress === ',');
  };

  // Sitting times are encoded in json. So we have to go deep through the areas and sessions to get to them
  const areaData = (restaurant?.areas || []).map((area) => ({
    ...area,
    sessions: (area?.sessions || []).map((session) => {
      return {
        ...session,
        sittingTimes: (session?.sittingTimes || []).map((sittingTime) => {
          const parsedSittingTime = safeParse(sittingTime);
          return {
            label: parsedSittingTime?.label,
            times: parsedSittingTime?.times,
          };
        }),
      };
    }),
  }));

  const joinedAreas = safeParse(restaurant?.bookingWidgetSettings)?.widgetSettings?.joinedAreas;

  return {
    ...restaurant,
    address: isValidAddress(restaurant?.address)
      ? trim(`${restaurant?.address}`.replace('\n', ', '))
      : null,
    areas: areaData,
    joinedAreas,
    venueDescription: theme.venueDescription,
    // ----------General----------
    daysInAdvance: nullIfNegative(restaurant.daysInAdvance) || 60,

    // ----------Widget settings----------
    ...widgetSettings,

    // ----------Other settings where lite and premium will differ----------
    iconBgColour: getPremiumSetting(isPremium, 'transparent', colors.liteIconBgColour),
    modalHeaderColour: getPremiumSetting(isPremium, '#ffffff', '#ffffff'),
    widgetHeaderBgColor: getPremiumSetting(isPremium, colors.white, colors.liteWidgetHeaderBgColor),
  };
};

export const isRestaurantClosedForDate = (date, sessions, closures, areas) => {
  let isClosed = false;

  const openDays = sessions.map((session) => session.day).filter(onlyUnique);
  const openDates = sessions.map((session) => session.date).filter(onlyUnique);

  if (
    !openDays.includes(date.format('ddd').toLowerCase()) &&
    !openDates.includes(date.format('YYYY-MM-DD'))
  ) {
    isClosed = true;
  }

  const isSameDay = (date1, dayCompare) =>
    dayCompare !== null && momentDaysOfWeek[date1.day()] === dayCompare;

  const isSameDate = (date1, dateCompare) =>
    dateCompare !== null && date1.isSame(moment(dateCompare), 'day');

  // Whether the given date is the same date (e.g. 01/01/2024) or the same day (e.g. wednesday)
  const isSameDayOrDate = (date1, dateCompare, dayCompare) =>
    (dateCompare !== null && date1.isSame(moment(dateCompare), 'day')) ||
    (dayCompare !== null && momentDaysOfWeek[date1.day()] === dayCompare);

  // Whether the area has any normal sessions
  const areaHasSessionsForDay = (area) => {
    const sessionsForAreaForDay = area.sessions.filter(
      (session) =>
        (session.startTime !== 0 || session.endTime !== 0) &&
        isSameDayOrDate(date, session.date, session.day),
    );

    return !isEmpty(sessionsForAreaForDay);
  };

  // If an area has a single "closed event" (date with start and end times 0), then the area is closed for the day
  const areaHasCloseEvent = (area) => {
    return area.sessions.some(
      (session) =>
        date.isSame(moment(session.date), 'day') &&
        session.startTime === 0 &&
        session.endTime === 0,
    );
  };

  const hasDefaultCloseDate = sessions.some(
    (session) =>
      session.startTime === 0 &&
      session.endTime === 0 &&
      (session.areaId === null || session.areaId <= 0) &&
      isSameDate(date, session.date),
  );

  const hasDefaultCloseDay = sessions.some(
    (session) =>
      session.startTime === 0 &&
      session.endTime === 0 &&
      (session.areaId === null || session.areaId <= 0) &&
      isSameDay(date, session.day),
  );

  // Does the venue have any "default" (non-area-specific) close events?
  const hasDefaultCloseEvent = hasDefaultCloseDate || hasDefaultCloseDay;

  // If every area has an event that counts as a closure, then the venue is considered closed for the day
  // Note: double negative. If any area is NOT closed, then the restaurant is open
  // Also if the area just has no sessions in general, then it's closed
  const allAreasClosed = !areas.some(
    (area) => areaHasSessionsForDay(area) && !areaHasCloseEvent(area),
  );

  // If there is a default closure event for the date or day
  if (hasDefaultCloseEvent) {
    // If the closure is for the day of the week, check if there are open sessions for this date
    if (hasDefaultCloseDay) {
      const hasOpenSessionForDate = sessions.some(
        (session) =>
          (session.startTime !== 0 || session.endTime !== 0) &&
          isSameDayOrDate(date, session.date, session.day),
      );

      // If there is an open session, the restaurant is open, otherwise it's closed
      isClosed = !hasOpenSessionForDate;
    }

    // Check if there is a specific closure for this date
    if (hasDefaultCloseDate) {
      const hasOpenSessionForDate = sessions.some(
        (session) =>
          (session.startTime !== 0 || session.endTime !== 0) &&
          isSameDayOrDate(date, session.date, session.day),
      );
      isClosed = !hasOpenSessionForDate;
    }
  }

  // If all areas are closed, then they're closed
  if (allAreasClosed) {
    isClosed = true;
  }

  if (isNil(closures)) {
    return isClosed;
  }

  const hasClosure = closures.some((closure) =>
    date.isBetween(moment(closure.startDate), moment(closure.endDate), 'day', '[]'),
  );

  if (hasClosure) {
    isClosed = true;
  }

  return isClosed;
};

export const getLogo = (isObee, variant = 'coloured') => {
  switch (variant) {
    case 'black': {
      return isObee ? <ObeeBlackIcon /> : <EatClubIcon style={{ color: '#313131' }} />; // todo: need black EatClub logo
    }
    case 'white': {
      return isObee ? <ObeeWhiteIcon /> : <EatClubIcon style={{ color: 'white' }} />; // todo: need white EatClub logo
    }
    case 'coloured':
    default: {
      return isObee ? <ObeeIcon /> : <EatClubIconColored />;
    }
  }
};
